import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Film, X } from 'lucide-react';
import { DOC_EDIT_NOTES_TARGET } from '@/lib/docEditNotes';

/**
 * "What's new": doc edit notes in the Style Profile, for CLIENTS who have ordered a doc edit and have none yet
 * (operator, 2026-10-05: their general notes were written for highlight films and reached the doc edits). One decision
 * on the screen: add doc edit notes, or not now.
 *
 * It blocks nobody, so every way out marks it seen, like the samples notice: the two buttons, the X, and the backdrop
 * (`dismissOnBackdrop`). "Add doc edit notes" marks it seen and goes straight to the box on the Style Profile
 * (StyleProfilePage.js, #doc-edit-notes).
 */
export function DocEditNotesAnnouncement({ onAcknowledge }) {
  const navigate = useNavigate();
  const seen = () => onAcknowledge && onAcknowledge();
  const addNotes = async () => { await seen(); navigate(DOC_EDIT_NOTES_TARGET); };
  return (
    <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-ug-raised border border-ug-border shadow-2xl"
         data-testid="whats-new-doc-edit-notes">
      <button type="button" onClick={seen} data-testid="whats-new-close" aria-label="Close"
              className="tap-auto absolute top-4 right-4 w-7 h-7 rounded-full flex items-center justify-center text-slate-500 hover:text-zinc-100 hover:bg-white/[0.06]">
        <X className="w-3.5 h-3.5" strokeWidth={2} />
      </button>
      <div className="p-6 pb-5 border-b border-ug-border-subtle">
        <div className="flex items-center gap-2 mb-3">
          <Film className="w-3.5 h-3.5 text-ug-gold" strokeWidth={2} />
        </div>
        <h2 className="font-heading text-2xl text-zinc-100 pr-8">Tell us how you like your doc edits</h2>
      </div>
      <div className="p-6 py-5 space-y-3">
        <p className="text-[14px] text-zinc-100/80 leading-relaxed" data-testid="whats-new-doc-edit-notes-body">
          Your Style Profile now has a box just for doc edits: what to keep in full, which speeches and dances, titles, sound and music.
        </p>
        <p className="text-[14px] text-zinc-100/80 leading-relaxed">
          Your editor reads it on every doc edit, so the notes you wrote for your highlight films stay with your highlight films.
        </p>
      </div>
      <div className="p-6 pt-5 border-t border-ug-border-subtle bg-ug-surface/20 flex flex-col sm:flex-row gap-2">
        <button data-testid="whats-new-next" onClick={addNotes}
                className="tap-auto flex-1 py-2.5 rounded-lg bg-gold-leaf text-obsidian font-sans text-[11px] font-semibold uppercase tracking-wider">
          Add doc edit notes
        </button>
        <button data-testid="whats-new-not-now" onClick={seen}
                className="tap-auto flex-1 py-2.5 rounded-lg border border-ug-border text-zinc-100/80 hover:text-zinc-100 hover:bg-white/[0.04] font-sans text-[11px] font-semibold uppercase tracking-wider">
          Not now
        </button>
      </div>
    </div>
  );
}
DocEditNotesAnnouncement.dismissOnBackdrop = true;

export default DocEditNotesAnnouncement;
